import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
  FormArray,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { CompanyProductDefaultTimeDistribution } from 'src/app/models/company_product_default_time_distribution.model';

import { CompanyProductPhase } from 'src/app/models/company_product_phase.model';
import { ApiService } from 'src/app/services/api.service';
import { SelectorDialogComponent } from '../../../../selector-dialog/selector-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'app-company-product-distribution-time-dialog',
  templateUrl: './company-product-distribution-time-dialog.component.html',
  styleUrls: ['./company-product-distribution-time-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatDialogActions,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatMenuModule,
  ],
})
export class CompanyProductDistributionTimeDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<CompanyProductDistributionTimeDialogComponent>>(MatDialogRef);
  data = inject<{
    company_product_id: number;
    companyProductDefaultTimeDistributionId: number;
    is_view: boolean;
  }>(MAT_DIALOG_DATA);
  private api = inject(ApiService);
  protected fb = inject(FormBuilder);
  private snack = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  company_product: CompanyProduct = null as any;
  distribution: CompanyProductDefaultTimeDistribution = null as any;
  form: FormGroup;

  async ngOnInit() {
    if (this.data.company_product_id == null) this.close();
    await this.fetchCompanyProduct();
    this.fetchDistributionTime();
  }

  close(): void {
    this.dialogRef.close();
  }

  save() {
    const distributionsData = this.form.get('distributions')?.value;
    const formattedData = this.formatDistributionsData(distributionsData);

    this.api
      .updateCompanyProductDefaultTimeDistribution(
        this.data.company_product_id.toString(),
        formattedData,
        this.data.companyProductDefaultTimeDistributionId
      )
      .subscribe(data => {
        this.close();
      });
  }

  getCurrentTotal(): number {
    const distributionsArray = this.getDistributionsArray();
    if (!distributionsArray) return 0;
    return distributionsArray.controls.reduce((sum: number, group: any) => {
      const timePrcnt = group.get('time_prcnt')?.value || 0;
      return sum + Number(timePrcnt);
    }, 0);
  }

  getDistributionsArray(): FormArray {
    return this.form.get('distributions') as FormArray;
  }

  getSumError(): string | null {
    const error = this.form.errors?.['sumNot100'];
    return error ? `La suma debe ser 100%, actualmente es ${error.actualSum}%` : null;
  }

  onClickAddPhase() {
    const phases = this.company_product.company_product_phases.filter(pp => {
      return (
        pp.company_product_phase_stage.slug !== 'start' &&
        pp.company_product_phase_stage.slug !== 'completed' &&
        !this.getDistributionsArray().controls.find(c => c.value.company_product_phase_id === pp.id)
      );
    });

    if (phases.length > 0) {
      this.dialog
        .open(SelectorDialogComponent, {
          width: '400px',
          data: {
            dialog_title: 'Seleccionar fase',
            dialog_ok_button: 'Seleccionar',
            dialog_cancel_button: 'Cancelar',
            data: phases.map(pp => {
              return {
                value: pp.id,
                textToShow: pp.company_product_phase_type.name,
              };
            }),
          },
        })
        .afterClosed()
        .subscribe((company_product_phase_id: number | undefined) => {
          if (company_product_phase_id) {
            const pp: CompanyProductPhase = this.company_product.company_product_phases.find(pp => pp.id === company_product_phase_id)!;
            this.api.addPhaseToDefaultTimeDistribution(this.company_product.id, this.distribution.id, pp.id).subscribe(data => {
              this.getDistributionsArray().push(
                this.fb.group({
                  id: data.id,
                  company_product_phase_id: pp.id,
                  time_prcnt: 0,
                  company_product_phase_name: pp.company_product_phase_type.name,
                })
              );
            });
          }
        });
    } else this.snack.open('No hay fases activas disponibles para añadir.', 'Ok', { duration: 3000 });
  }

  onClickRemovePhase(idx: number) {
    const data = this.getDistributionsArray().controls[idx].value;
    if (confirm(`¿Estás seguro que quieres eliminar la fase '${data.company_product_phase_name}'?`)) {
      this.getDistributionsArray().removeAt(idx);
      this.api
        .removePhaseFromDefaultTimeDistribution(this.company_product.id, this.distribution.id, data.company_product_phase_id)
        .subscribe(() => {});
    }
  }

  private checkDistributionAreAllPhases() {
    let areAllPhases: boolean = true;
    this.distribution.company_product_default_time_distribution_distributables.forEach(d => {
      if (d.distributable_type !== 'App\\Models\\CompanyProductPhase') {
        areAllPhases = false;
      }
    });

    if (!areAllPhases) {
      this.snack.open('Error: Hay algún distributable que no es de fase.', 'Ok', { duration: 3000 });
    }
  }

  private fetchDistributionTime(): void {
    this.api
      .getCompanyProductDefaultTimeDistribution(this.data.company_product_id.toString(), this.data.companyProductDefaultTimeDistributionId)
      .subscribe((data: CompanyProductDefaultTimeDistribution): void => {
        this.distribution = data;
        this.checkDistributionAreAllPhases();
        this.initForm();
      });
  }

  private async fetchCompanyProduct() {
    const data = await this.api.getCompanyProduct(this.data.company_product_id.toString(), { with: 'company_product_phases' }).toPromise();
    if (data) this.company_product = data;
    else this.close();
  }

  private initForm() {
    const formArray = this.fb.array(
      this.distribution.company_product_default_time_distribution_distributables.map(pdtdd => {
        return this.fb.group({
          id: [pdtdd.id],
          company_product_phase_id: [pdtdd.distributable_id],
          time_prcnt: [pdtdd.time_prcnt, [Validators.required, Validators.min(0), Validators.max(100)]],
          company_product_phase_name: [(pdtdd.distributable as CompanyProductPhase).company_product_phase_type.name],
        });
      })
    );

    this.form = this.fb.group(
      {
        distributions: formArray,
      },
      { validators: this.sumValidator }
    );

    if (this.data.is_view) {
      this.form.disable();
    }
  }

  private sumValidator(control: AbstractControl): ValidationErrors | null {
    const distributions = control.get('distributions') as FormArray;
    if (!distributions) return null;

    const total = distributions.controls.reduce((sum: number, group: AbstractControl) => {
      const timePrcnt = group.get('time_prcnt')?.value || 0;
      return sum + Number(timePrcnt);
    }, 0);

    return total === 100 ? null : { sumNot100: { actualSum: total, requiredSum: 100 } };
  }

  private formatDistributionsData(distributions: any[]): any {
    const formattedData: any = {};

    distributions.forEach(distribution => {
      const key = `company_product_phase_${distribution.company_product_phase_id}`;
      formattedData[key] = {
        prcent: distribution.time_prcnt,
      };
    });

    return formattedData;
  }
}
